import { FC, memo, useMemo, useState } from 'react'

import { Button, SelectChangeEvent, Skeleton, Stack, Typography, useTheme } from '@mui/material'
import { CloudQueue, Home } from '@mui/icons-material'

import { useTranslation } from 'react-i18next'
import PerfectScrollbar from 'react-perfect-scrollbar'

import { useMentorCloudFolders } from 'src/hooks/files/useMentorCloudFolders'

import CustomSelect, { CustomSelectOption } from 'src/components/common/select/CustomSelect'

import { LOCAL_STORAGE_MENTOR_CLOUD_FOLDER_SORT } from 'src/utils/mediaStorage/constants'

import { MentorCloudFolder } from '../MentorCloudFolder/MentorCloudFolder'

import { Folder } from 'src/types/files/file'

interface MentorCloudSidebarProps {
  activeFolder?: Folder | null
  onChangeActiveFolder: (folder?: Folder | null) => void
  onChangeActiveSubFolder: (folder?: Folder | null) => void
}

const folderSortOptions: CustomSelectOption[] = [
  {
    name: 'sort.a-z',
    value: 'a-z'
  },
  {
    name: 'sort.z-a',
    value: 'z-a'
  },
  {
    name: 'sort.newest',
    value: 'newest'
  },
  {
    name: 'sort.oldest',
    value: 'oldest'
  }
]

const skeletons = new Array(8).fill(0)

type FolderSort = 'a-z' | 'z-a' | 'newest' | 'oldest'

const sortFolders = (folders: Folder[], sort: FolderSort) => {
  switch (sort) {
    case 'a-z':
      return folders.sort((a, b) => a.name.localeCompare(b.name))
    case 'z-a':
      return folders.sort((a, b) => b.name.localeCompare(a.name))
    case 'newest':
      return folders.sort((a, b) => new Date(b.created_at_epoch).getTime() - new Date(a.created_at_epoch).getTime())
    case 'oldest':
      return folders.sort((a, b) => new Date(a.created_at_epoch).getTime() - new Date(b.created_at_epoch).getTime())
    default:
      return
  }
}

export const MentorCloudSidebar: FC<MentorCloudSidebarProps> = memo(
  ({ activeFolder, onChangeActiveFolder, onChangeActiveSubFolder }: MentorCloudSidebarProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'PAGE.MENTORCLOUD' })

    const theme = useTheme()

    const [folderSort, setFolderSort] = useState<FolderSort>(
      (localStorage.getItem(LOCAL_STORAGE_MENTOR_CLOUD_FOLDER_SORT) as FolderSort) || 'a-z'
    )

    const onChangeFolderSort = (event: SelectChangeEvent<string | number | undefined>) => {
      setFolderSort(event.target.value as FolderSort)
      localStorage.setItem(LOCAL_STORAGE_MENTOR_CLOUD_FOLDER_SORT, event.target.value as FolderSort)
    }

    const onClickHome = () => {
      onChangeActiveFolder(null)
      onChangeActiveSubFolder(null)
    }
    const onClickFolder = (folder?: Folder | null) => {
      if (activeFolder?.id === folder?.id) {
        onChangeActiveSubFolder(undefined)
        onChangeActiveFolder(undefined)

        return
      }
      onChangeActiveSubFolder(folder)
      onChangeActiveFolder(folder)
    }

    const { data: folders = [], isLoading } = useMentorCloudFolders()

    const sortedFolders = useMemo(() => sortFolders(folders, folderSort), [folderSort, folders])

    return (
      <Stack minWidth={theme.spacing(64)} gap={theme.spacing(2)} borderRight={`1px solid ${theme.palette.divider}`}>
        <Stack
          direction={'row'}
          borderRadius={theme.spacing(1.5)}
          alignItems={'center'}
          bgcolor={'linear-gradient(45deg, #415BF5 0%, #6E82F7 100%)'}
          gap={theme.spacing(3)}
          padding={theme.spacing(6)}
          justifyContent={'center'}
          color={theme.palette.getContrastText(theme.palette.primary[500])}
          sx={{
            background: `linear-gradient(45deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[300]} 100%)`,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0
          }}
        >
          <CloudQueue fontSize={'large'} color={'inherit'} />
          <Typography variant='h4' color={theme.palette.getContrastText(theme.palette.primary[500])}>
            {t('title')}
          </Typography>
        </Stack>
        <Stack gap={theme.spacing(2)} padding={theme.spacing(0, 2)}>
          <CustomSelect
            variant='outlined'
            handleValueChange={onChangeFolderSort}
            selectItems={folderSortOptions.map(option => ({ ...option, name: t(option.name) }))}
            value={folderSort}
          />
          <Button
            onClick={onClickHome}
            variant={activeFolder === null ? 'contained' : 'tonal'}
            startIcon={<Home />}
            sx={{ justifyContent: 'start' }}
          >
            {t('home')}
          </Button>
          <Stack sx={{ flex: 1, minHeight: 0, maxHeight: `calc(100vh - ${theme.spacing(74)})` }}>
            <PerfectScrollbar>
              <Stack gap={theme.spacing(2)} paddingBottom={theme.spacing(2)}>
                {isLoading &&
                  skeletons.map((_v, i) => (
                    <Skeleton
                      key={i}
                      width={'100%'}
                      height={theme.spacing(10)}
                      animation='pulse'
                      style={{ transform: 'none' }}
                    />
                  ))}
                {sortedFolders?.map(folder => (
                  <MentorCloudFolder
                    key={folder.id}
                    isActive={activeFolder?.id === folder.id}
                    onClick={onClickFolder}
                    onClickSubFolder={onChangeActiveSubFolder}
                    folder={folder}
                  />
                ))}
              </Stack>
            </PerfectScrollbar>
          </Stack>
        </Stack>
      </Stack>
    )
  }
)
